import { useCallback, useMemo, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';

import withJsonLdProduct from 'components/common/JsonLDProductWrapper';
import CardGender from 'components/common/card/CardGender';
import { cn } from 'helpers/classnames';
import Badge from 'components/common/Badge';
import useMartyContext from 'hooks/useMartyContext';
import Link from 'components/common/MartyLink';
import { fetchProductDetail } from 'actions/productDetail';
import { makeAscii } from 'helpers';
import Card from 'components/common/card/Card';
import CardMedia from 'components/common/card/CardMedia';
import CardPrice from 'components/common/card/CardPrice';
import Rating from 'components/Rating';
import SponsoredBanner from 'components/common/SponsoredBanner';
import getCardData from 'data/getCardData';
import Hearts from 'components/common/Hearts';
import LowStockLabel from 'components/common/melodyCard/LowStockLabel';
import { SEARCH_IMGS_NOT_LAZY_LOADED } from 'constants/appConstants';
import { getCardFlag } from 'helpers/cardUtils';
import ProductUtils from 'helpers/ProductUtils';
import useWindowSize from 'hooks/useWindowSize';
import Tag from 'tailwind/components/Tag/Tag';
import { isMobileViewport } from 'utils/viewportUtil';
import { isSupportedMobileDevice } from 'helpers/UserAgentUtils';
import { FEATURE_LEFT_IN_STOCK } from 'constants/features';
import { ZAPPOS_MERCHANTID } from 'constants/crossSiteMerchantIdMapping';
import { selectUnleashToggle } from 'selectors/features';
import { PromoBadge } from 'components/common/PromoBadge/PromoBadge';
import { useCanHover } from 'hooks/useCanHover';
import { useCardSwipe } from 'hooks/useCardSwipe';
import { usePreloadAdjacentImages } from 'hooks/usePreloadAdjacentImages';
import { useSwatchSizing } from 'hooks/useSwatchSizing';
import { useIsInCarousel } from 'components/common/ProductCarousel/ProductCarousel.context';
import { track } from 'apis/amethyst';
import { evProductImageInteraction } from 'events/search';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/components/common/productCard.scss';
/**
 * Attach to onMouseEntered and onMouseLeave to have a debounced notification.
 * use with `const [hovered, setHovered] = useState(false);` to get a react re-render
 *
 *  onMouseEntered: (e) => handleMouseHover(e, true, setHovered)
 *  onMouseLeave:   (e) => handleMouseHover(e, false, setHovered)
 *
 * @param event    event that fired
 * @param hovered  boolean
 * @param setHovered (hovered: boolean) => any   function to call when state changes
 */
function handleMouseHovered(event, hovered, setHovered) {
  const element = event.target;
  if (element.__productCardHoverTimer) {
    clearTimeout(element.__productCardHoverTimer);
    element.__productCardHoverTimer = null;
  }
  if (hovered) {
    element.__productCardHoverTimer = setTimeout(() => {
      setHovered(true);
    }, 500);
  } else {
    setHovered(false);
  }
}

const ProductCard = props => {
  const {
    index,
    linkProps,
    className,
    brandName,
    intersectionRef,
    isSponsored,
    onClick,
    onMediaHovered,
    productName,
    productSeoUrl,
    productUrl,
    styleColor,
    styleId,
    CardDetailsTopSlot,
    CardDetailsTopSlot2,
    CardBottomSlot,
    onHand,
    productId,
    colorId,
    imageClassName,
    isLinkDisabled = false,
    mediaChildren,
    showPrice = true,
    showColorName = false,
    makeStarsBlock,
    badges = [],
    showRecosBadge = false,
    imageBadgeClassName,
    isMobileBlockLayout = false,
    showProductTag = false,
    productTagCount = 0,
    dateLabel,
    preLabel = '',
    includeSwatchRowSpace = false,
    productType,
    clickThruUrl
  } = props;

  const {
    testId,
    marketplace: {
      merchantId,
      search: { showRatingStars, showLowStockLabel }
    }
  } = useMartyContext();

  const unleashLeftInStockFeature = useSelector(state => selectUnleashToggle(FEATURE_LEFT_IN_STOCK, state));
  const isZappos = merchantId === ZAPPOS_MERCHANTID; // check if the merchant is zappos

  // useSwatchSizing resolves the PLP redesign enrollment (FEATURE_PLP_PRODUCT_CARD_UI_UPDATES, isZapposOnly)
  // alongside the swatch geometry. swatchContainerStyle feeds the standalone mimicSwatchWrapper's reserved footprint.
  const { isPlpProductCardRedesignEnabled, swatchContainerStyle } = useSwatchSizing();

  // Get promo badges for the current product style
  const promoBadges = useMemo(() => ProductUtils.getPromoBadgesForStyle(props), [props.styleId, props.promoBadges]);

  const [isHovered, setHovered] = useState(() => false);
  const [badgeAccessibilityLabel, setBadgeAccessibilityLabel] = useState('');
  const displayRatingStars = showRatingStars;
  const { media, price, reviews, gender } = getCardData({
    ...props,
    isHovered,
    showRatingStars: displayRatingStars,
    // Zappos gets the multi-image preview: build the angle set and skip the legacy single hover image
    // it supersedes. 6pm skips the angle work and keeps the legacy hover image.
    computeMultiImageAngles: isZappos
  });

  // Extract all the images for the current product style and memoize for quick access.
  // Scoped to Zappos: on 6pm this stays null so cards fall back to the legacy single-image render.
  const allImages = useMemo(() => (isZappos ? (media.allImages ?? null) : null), [styleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ordered { angle, imageId } metadata, index-aligned with allImages, for swipe analytics.
  const imageAngles = useMemo(() => (isZappos ? (media.imageAngles ?? null) : null), [styleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Multi-image-eligible cards derive desktop hover inside CardMedia (via useIsMouseOver),
  // so the legacy hover handlers below are skipped for them.
  const isMultiImagePreviewEligible = allImages?.length > 1;

  // Touch-only (no-hover) eligible cards get swipe navigation; hover-capable devices use
  // the desktop rotation handled inside CardMedia.
  const isHoverSupported = useCanHover();
  // Cards rendered inside a carousel/scroller (ProductDetailCarousel, MarketRecos) must not capture the
  // horizontal touch gesture for image swipe — the container owns it. Grid surfaces (PLP organic grid,
  // PLP inline recos/sponsored) don't provide the context, so swipe stays enabled there.
  const inCarousel = useIsInCarousel();
  const isSwipeSupported = isMultiImagePreviewEligible && !isHoverSupported && !inCarousel;

  // Track swipe interactions on multi-image cards on mobile.
  const onImageSwipe = useCallback(
    ({ direction, index }) => {
      const interactedImage = imageAngles?.[index];
      if (!interactedImage) {
        return; // no angle metadata resolved → don't emit a partial event
      }

      track(() => [
        evProductImageInteraction,
        {
          swipeDirection: direction,
          interactedProduct: { productId, styleId, colorId },
          interactedImage: { imageId: interactedImage.imageId, angle: interactedImage.angle }
        }
      ]);
    },
    [imageAngles, productId, styleId, colorId]
  );

  // Boundary for scoping the swipe to the image: a touch starting at/below the details top won't arm it.
  const cardDetailsWrapperRef = useRef(null);

  const {
    activeIndex: mobileImageIndex,
    cardRef,
    didSwipeRef
  } = useCardSwipe({
    total: allImages?.length ?? 0,
    allImages: allImages ?? [],
    enabled: isSwipeSupported,
    onSwipe: onImageSwipe,
    boundaryRef: cardDetailsWrapperRef,
    // useCardSwipe merges this (the IntersectionObserver ref) with its own listener host and returns a
    // single callback ref, so the card keeps view-tracking while the overlay Link stays full-card.
    forwardedRef: intersectionRef
  });

  // After the first swipe, warm the next/prev angles so subsequent swipes are cache hits.
  usePreloadAdjacentImages({
    images: allImages ?? [],
    currentIndex: mobileImageIndex,
    enabled: isSwipeSupported && mobileImageIndex > 0
  });

  const isProductTypeShoeAndClothing = ProductUtils.isProductTypeShoesOrClothing(productType);

  const productLink = productSeoUrl || productUrl || null;
  const encodedBrandName = makeAscii(brandName);
  const encodedProductName = makeAscii(productName);
  const encodedPreLabel = makeAscii(preLabel);

  // take a 'HeartsData' | 'HeartProps' type as the 'hearts' argument.
  // todo: remove the 'HeartsData' type from the code.
  const heartsData = props.hearts?.heartsData || props.hearts;

  // ProductEventData for Amplitude tracking in hearts
  const productEventData = {
    brand: brandName,
    category: productType,
    gender,
    list_price: price.msrp?.string,
    name: productName,
    on_sale: price.onSale,
    position: index + 1,
    price: price.price?.string,
    sku: productId,
    styleId,
    index
  };

  const heartProps = {
    // todo: flatten these 'hearts' properties
    onHeartClick: heartsData?.onHeartClick,
    showFavoriteHeart: heartsData?.showFavoriteHeart,
    isDisplayCount: false,
    styleId,
    productId,
    productEventData
  };
  const { width: windowWidth = 0 } = useWindowSize();
  const isMobile = isMobileViewport(windowWidth);

  const clickHandler = e => {
    // A committed swipe fires a click on release; swallow it so the card doesn't navigate.
    if (didSwipeRef.current) {
      e.preventDefault();
      didSwipeRef.current = false;
      return;
    }
    onClick?.(e, styleId);
  };

  const flags = {
    sponsored: <SponsoredBanner newCard={true} index={index} />
  };

  const { variant: { name, enabled } = { name: '', enabled: false } } = unleashLeftInStockFeature || {};
  const isLeftInStockEnabled = merchantId === ZAPPOS_MERCHANTID && enabled && name === '1';
  const flagType = getCardFlag({ ...props });
  const flag = flags[flagType] || null;
  const hasLowStock = onHand <= 10;
  const displayLowStockLabel = !!showLowStockLabel && hasLowStock && !isLeftInStockEnabled;
  const productLabel = ProductUtils.getProductLabel(
    encodedProductName,
    encodedBrandName,
    styleColor,
    isSponsored,
    !!badges.length,
    displayLowStockLabel,
    encodedPreLabel
  );

  const makeStars = () => {
    if (!displayRatingStars) {
      return null;
    }

    // Treatment omits the rating entirely when there is no rating so the row reserves no vertical
    // space (mirrors the rating check inside the Rating component); control keeps the reserved row.
    if (isPlpProductCardRedesignEnabled && !(parseInt(String(reviews.roundedRating)) > 0)) {
      return null;
    }

    return (
      <>
        <dt>Rating</dt>
        <dd>
          <Rating
            rating={reviews.roundedRating}
            additionalClasses={makeStarsBlock && css.pdpCardReviews}
            reviewCount={reviews.ratingCount}
            hasDisplayReviewCount={true}
          />
        </dd>
      </>
    );
  };

  const setBadgeLabelHandler = useCallback(
    label => {
      setBadgeAccessibilityLabel(label);
    },
    [setBadgeAccessibilityLabel]
  );

  // check if the zappos application has a global banner
  const hasGlobalBanner = useSelector(state => {
    const slots = state.headerFooter?.dualGlobalBanners?.data?.slotData;
    return !!(slots?.['banner-1'] || slots?.['banner-2']);
  });

  /**
   * if there is a global banner then we want to ensure that this is prioritized to be eagerly loaded on the desktop view
   *  whereas the product cards in the PLP Grid are lazily loaded so that we ensure the LCP is maintained.
   * But if there is no global banner then we prioritize the first two rows to be eagerly loaded.
   * And for the mobile-view we prioritize the first 4 product cards irrespective of the global banner.
   */
  const userAgent = useSelector(state => state.url?.userAgent);
  const isMobileUA = isSupportedMobileDevice(userAgent || '');
  const eagerThreshold = isMobileUA ? 4 : hasGlobalBanner ? 0 : 8;
  const imageLoadingStrategy = index < eagerThreshold ? 'eager' : 'lazy';

  // Legacy hover-image crossfade applies only to non-eligible cards (flag-off / single-image).
  // Multi-image-eligible cards derive desktop hover inside CardMedia via useIsMouseOver, so
  // attaching these handlers would only cause needless ProductCard re-renders for that path.
  const legacyHoverHandlers = isMultiImagePreviewEligible
    ? {}
    : {
        onMouseEnter: e => handleMouseHovered(e, true, setHovered),
        onMouseLeave: e => handleMouseHovered(e, false, setHovered)
      };

  // Swipe cards attach native listeners via cardRef on the card; they add no React touch
  // handlers here. Non-swipe cards keep the legacy hover handlers. The two paths are mutually exclusive.
  const mediaInteractionHandlers = isSwipeSupported ? {} : legacyHoverHandlers;

  return (
    <Card
      className={className}
      // For gating the PLP product card redesign styles behind the A/B flag https://taskei.amazon.dev/tasks/FEE-8350
      data-plp-product-card-redesign={isPlpProductCardRedesignEnabled ? '' : undefined}
      // For helping site merch collect styleIds https://github.zappos.amazon.dev/mweb/marty/pull/9699
      data-style-id={styleId}
      data-test-id={props['data-test-id']}
      data-low-stock={displayLowStockLabel}
      ref={isSwipeSupported ? cardRef : intersectionRef}
    >
      <Link
        className={cn(css.productLink, {
          [css.swipeEnabled]: isSwipeSupported,
          [css.mobileBlock]: isMobileBlockLayout
        })}
        data-style-id={styleId}
        to={clickThruUrl ?? (productLink || '')}
        data-test-id={testId('searchResultLink')}
        onClick={clickHandler}
        {...mediaInteractionHandlers}
        isLinkDisabled={isLinkDisabled}
        {...linkProps}
      >
        {badges.length > 0 && badgeAccessibilityLabel}
        {productLabel} {price.label}. {reviews.label}
      </Link>
      <CardMedia
        onHover={onMediaHovered}
        forceLoadIndex={SEARCH_IMGS_NOT_LAZY_LOADED}
        {...media}
        allImages={allImages}
        mobileImageIndex={mobileImageIndex}
        styleId={styleId}
        imageLoadingStrategy={imageLoadingStrategy}
        imageClassName={cn(imageClassName, imageBadgeClassName)}
        disableHoverImage={isSponsored}
      >
        <div className={css.top}>
          {!showRecosBadge && !flag && badges.length > 0 && (
            <Badge setLabel={setBadgeLabelHandler} id={badges[0].bid} category={badges[0].zc} url={badges[0].url} productLink={productLink} />
          )}
          {showRecosBadge && badges.length > 0 && (
            <Badge setLabel={setBadgeLabelHandler} id={badges[0].bid} category={badges[0].zc} url={badges[0].url} productLink={productLink} />
          )}
          {flag}
          {!!dateLabel && (
            <div className={cn(css.dateLabel, { [css.disableLink]: isLinkDisabled })}>
              <Tag size={isMobile ? 'medium' : 'large'} variant="white">
                {dateLabel}
              </Tag>
            </div>
          )}
          <Hearts {...heartProps} />
        </div>
        {mediaChildren}
        <div className={css.bottom}>
          {displayLowStockLabel && <LowStockLabel />}
          {showProductTag && <div className={css.tagBadge}>{productTagCount}</div>}
        </div>
      </CardMedia>
      <div ref={cardDetailsWrapperRef} className={css.detailsWrapper}>
        {!!CardDetailsTopSlot && <CardDetailsTopSlot {...props} />}
        {!!CardDetailsTopSlot2 && <CardDetailsTopSlot2 {...props} />}
        {includeSwatchRowSpace && <span className={css.mimicSwatchWrapper} style={swatchContainerStyle} />}
        <Link
          className={cn(css.details, {
            [css.mobileBlock]: isMobileBlockLayout
          })}
          onClick={clickHandler}
          tabIndex={-1}
          to={clickThruUrl ?? (productLink || '')}
          isLinkDisabled={isLinkDisabled}
        >
          <dl>
            <dt>Brand Name</dt>
            <dd className={css.mainText} data-test-id={testId('brand')}>
              <span>{encodedBrandName}</span>
            </dd>
            <dt>Product Name</dt>
            <dd className={css.subText} data-test-id={testId('productName')}>
              {encodedProductName}
            </dd>
            {gender && isProductTypeShoeAndClothing && <CardGender gender={gender} />}
            <dt>Color</dt>
            <dd className={showColorName ? css.colorText : css.colorName} data-test-id={testId('colorName')}>
              {styleColor}
            </dd>
            {showPrice && <CardPrice {...price} isAvailable={onHand > 0} url={productLink} />}
            {promoBadges?.length > 0 && (
              // Treatment tightens the promo vertical margin to 4px (my-1); control keeps 8px (my-2).
              <div className={isPlpProductCardRedesignEnabled ? 'my-1' : 'my-2'}>
                {promoBadges.map((badge, index) => (
                  <PromoBadge key={index} {...badge} />
                ))}
              </div>
            )}
            {makeStars()}
          </dl>
        </Link>
        {!!CardBottomSlot && <CardBottomSlot {...props} onClick={clickHandler} />}
      </div>
    </Card>
  );
};

const mapDispatchToProps = {
  fetchProductDetail
};

const mapStateToProps = state => {
  const { headerFooter: { content } = {}, filters: { term } = {} } = state;

  return {
    term,
    content
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withJsonLdProduct(ProductCard));
